import React, { useState, useEffect, useCallback } from 'react';

const CustomMindmapViewer = ({ mindmapData }) => {
    const [scale, setScale] = useState(1);
    const [center, setCenter] = useState({ x: 100, y: 50 });
    const [collapsed, setCollapsed] = useState(new Set());
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [processedData, setProcessedData] = useState(null);
    const [draggingNode, setDraggingNode] = useState(null);
    const [nodeOffsets, setNodeOffsets] = useState({});

    const getNodeColor = (type) => {
        switch (type) {
            case 'main-topic': return '#2563eb';
            case 'subtopic': return '#7c3aed';
            case 'definition': return '#dc2626';
            case 'example': return '#58BA51';
            case 'code': return '#0000ab';
            default: return '#6b7280';
        }
    };

    // Toggle node collapse state
    const toggleNode = (nodeId) => {
        setCollapsed(prev => {
            const newCollapsed = new Set(prev);
            if (newCollapsed.has(nodeId)) {
                newCollapsed.delete(nodeId);
            } else {
                newCollapsed.add(nodeId);
            }
            return newCollapsed;
        });
    };

    // Calculate positions for the mindmap layout
    const calculateLayout = useCallback((node, x = 0, y = 0, level = 0) => {
        const VERTICAL_SPACING = 180;
        const HORIZONTAL_SPACING = 100;
        const NODE_WIDTH = 280;

        const words = (node.text || '').split(' ').filter(word => word.length > 0);
        const CHARS_PER_LINE = 30;
        const lines = [];

        if (words.length === 0) {
            lines.push('');
        } else {
            let currentLine = words[0];
            for (let i = 1; i < words.length; i++) {
                if ((currentLine + ' ' + words[i]).length <= CHARS_PER_LINE) {
                    currentLine += ' ' + words[i];
                } else {
                    lines.push(currentLine);
                    currentLine = words[i];
                }
            }
            lines.push(currentLine);
        }

        const nodeHeight = Math.max(80, lines.length * 24 + 20);

        node.x = x;
        node.y = y;
        node.width = NODE_WIDTH;
        node.height = nodeHeight;
        node.textLines = lines;

        if (!node.children || collapsed.has(node.id)) return { node, width: NODE_WIDTH, height: nodeHeight };

        let totalWidth = 0;
        let maxChildHeight = 0;

        const processedChildren = node.children.map((child) => {
            const childResult = calculateLayout(
                child,
                x + totalWidth,
                y + VERTICAL_SPACING,
                level + 1
            );

            totalWidth += childResult.width + HORIZONTAL_SPACING;
            maxChildHeight = Math.max(maxChildHeight, childResult.height);
            return childResult.node;
        });

        node.x = x + (totalWidth - HORIZONTAL_SPACING - NODE_WIDTH) / 2;
        node.children = processedChildren;

        return {
            node,
            width: Math.max(NODE_WIDTH, totalWidth - HORIZONTAL_SPACING),
            height: VERTICAL_SPACING + maxChildHeight
        };
    }, [collapsed]);

    const handleWheel = useCallback((e) => {
        e.preventDefault();

        const svgRect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX - svgRect.left;
        const mouseY = e.clientY - svgRect.top;

        const zoomPoint = {
            x: (mouseX - center.x) / scale,
            y: (mouseY - center.y) / scale
        };

        const ZOOM_SENSITIVITY = 0.001;
        const newScale = Math.min(Math.max(
            scale * (1 - e.deltaY * ZOOM_SENSITIVITY),
            0.5
        ), 2);

        const newCenter = {
            x: mouseX - zoomPoint.x * newScale,
            y: mouseY - zoomPoint.y * newScale
        };

        setScale(newScale);
        setCenter(newCenter);
    }, [scale, center]);

    // Draw connecting lines between nodes
    const renderConnections = (node) => {
        if (!node.children || collapsed.has(node.id)) return null;

        const nodeOffset = nodeOffsets[node.id] || { x: 0, y: 0 };

        return node.children.map((child) => {
            const childOffset = nodeOffsets[child.id] || { x: 0, y: 0 };

            return (
                <React.Fragment key={`connection-${node.id}-${child.id}`}>
                    <path
                        d={`M ${node.x + nodeOffset.x + node.width / 2} ${node.y + nodeOffset.y + node.height}
                           C ${node.x + nodeOffset.x + node.width / 2} ${(node.y + nodeOffset.y + child.y + childOffset.y + node.height) / 2},
                             ${child.x + childOffset.x + child.width / 2} ${(node.y + nodeOffset.y + child.y + childOffset.y + node.height) / 2},
                             ${child.x + childOffset.x + child.width / 2} ${child.y + childOffset.y}`}
                        stroke={getNodeColor(child.type)}
                        strokeWidth="2"
                        fill="none"
                        className={draggingNode ? '' : 'transition-all duration-75'}
                    />
                    {renderConnections(child)}
                </React.Fragment>
            );
        });
    };

    // Add this new function to handle node dragging
    const handleNodeDrag = useCallback((nodeId, e) => {
        e.stopPropagation();
        if (!draggingNode) {
            setDraggingNode({
                id: nodeId,
                startX: e.clientX,
                startY: e.clientY
            });
        }
    }, [draggingNode]);

    // Update the handleNodeDragMove function for more immediate response
    const handleNodeDragMove = useCallback((e) => {
        if (draggingNode) {
            e.stopPropagation();
            const dx = (e.clientX - draggingNode.startX) / scale;
            const dy = (e.clientY - draggingNode.startY) / scale;

            // Simplified update function for better performance
            const updateOffsets = (currentNodeId) => {
                const findNode = (node) => {
                    if (node.id === currentNodeId) return node;
                    if (!node.children) return null;
                    for (const child of node.children) {
                        const found = findNode(child);
                        if (found) return found;
                    }
                    return null;
                };

                const node = findNode(processedData);
                if (!node) return;

                // Update all nodes in a single state update
                setNodeOffsets(prev => {
                    const updates = { ...prev };

                    const updateNodeAndChildren = (n) => {
                        updates[n.id] = {
                            x: (prev[n.id]?.x || 0) + dx,
                            y: (prev[n.id]?.y || 0) + dy
                        };

                        if (n.children && !collapsed.has(n.id)) {
                            n.children.forEach(updateNodeAndChildren);
                        }
                    };

                    updateNodeAndChildren(node);
                    return updates;
                });
            };

            updateOffsets(draggingNode.id);

            // Update drag start position immediately
            setDraggingNode({
                ...draggingNode,
                startX: e.clientX,
                startY: e.clientY
            });
        }
    }, [draggingNode, scale, processedData, collapsed]);

    // Add this function to handle node drag end
    const handleNodeDragEnd = useCallback(() => {
        setDraggingNode(null);
    }, []);

    // Modify the renderNode function to include dragging functionality
    const renderNode = (node) => {
        const color = getNodeColor(node.type);
        const hasChildren = node.children && node.children.length > 0;
        const textLines = node.textLines || [''];
        const offset = nodeOffsets[node.id] || { x: 0, y: 0 };

        const LINE_HEIGHT = 24;
        const totalTextHeight = textLines.length * LINE_HEIGHT;
        const startY = node.height / 2 - (totalTextHeight / 2) + (LINE_HEIGHT / 2);

        return (
            <React.Fragment key={node.id}>
                <g
                    transform={`translate(${node.x + offset.x},${node.y + offset.y})`}
                    className={draggingNode ? '' : 'transition-transform duration-75'}
                    onMouseDown={(e) => handleNodeDrag(node.id, e)}
                    style={{ cursor: 'move' }}
                >
                    <rect
                        width={node.width}
                        height={node.height}
                        rx="8"
                        className="fill-white"
                        stroke={color}
                        strokeWidth="2"
                    />
                    {textLines.map((line, index) => (
                        <text
                            key={index}
                            x={node.width / 2}
                            y={startY + (index * LINE_HEIGHT)}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className="text-sm font-medium select-none"
                            style={{ fill: color }}
                        >
                            {line}
                        </text>
                    ))}
                    {hasChildren && (
                        <g
                            transform={`translate(${node.width / 2}, ${node.height - 15})`}
                            className="cursor-pointer"
                            onClick={() => toggleNode(node.id)}
                        >
                            <circle
                                r="10"
                                className="fill-white"
                                stroke={color}
                            />
                            <text
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="text-xs"
                                style={{ fill: color }}
                            >
                                {collapsed.has(node.id) ? '+' : '−'}
                            </text>
                        </g>
                    )}
                </g>
                {!collapsed.has(node.id) && node.children?.map(child => renderNode(child))}
            </React.Fragment>
        );
    };

    // Process mindmap data when it changes
    useEffect(() => {
        if (mindmapData) {
            console.log("Processing mindmap data:", mindmapData);
            const layoutData = calculateLayout(mindmapData);
            setProcessedData(layoutData.node);
        }
    }, [mindmapData, calculateLayout]);

    if (!mindmapData) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-black text-purple-400">
                <p>Select a topic to generate a mindmap</p>
            </div>
        );
    }

    if (!processedData) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-black text-purple-400">
                <p>Processing mindmap data...</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full overflow-hidden bg-black rounded-lg flex">
            <div className="flex-1 flex flex-col">
                <div className="flex gap-4 p-4 border-b border-purple-900/30">
                    <button
                        onClick={() => setScale(s => Math.min(s * 1.2, 2))}
                        className="px-3 py-1 rounded bg-purple-600/20 text-purple-400 hover:bg-purple-600/30"
                    >
                        Zoom In
                    </button>
                    <button
                        onClick={() => setScale(s => Math.max(s / 1.2, 0.5))}
                        className="px-3 py-1 rounded bg-purple-600/20 text-purple-400 hover:bg-purple-600/30"
                    >
                        Zoom Out
                    </button>
                    <button
                        onClick={() => {
                            setCenter({ x: 100, y: 50 });
                            setScale(0.8);
                        }}
                        className="px-3 py-1 rounded bg-purple-600/20 text-purple-400 hover:bg-purple-600/30"
                    >
                        Reset View
                    </button>
                </div>

                <div className="flex-1 relative">
                    <svg
                        width="100%"
                        height="100%"
                        className="w-full h-full bg-black"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        onWheel={handleWheel}
                        onMouseDown={(e) => {
                            if (!draggingNode) {
                                setIsDragging(true);
                                setDragStart({
                                    x: e.clientX - center.x,
                                    y: e.clientY - center.y
                                });
                            }
                        }}
                        onMouseMove={(e) => {
                            if (isDragging) {
                                setCenter({
                                    x: e.clientX - dragStart.x,
                                    y: e.clientY - dragStart.y
                                });
                            }
                            handleNodeDragMove(e);
                        }}
                        onMouseUp={() => {
                            setIsDragging(false);
                            handleNodeDragEnd();
                        }}
                        onMouseLeave={() => {
                            setIsDragging(false);
                            handleNodeDragEnd();
                        }}
                    >
                        <g transform={`translate(${center.x},${center.y}) scale(${scale})`}>
                            {renderConnections(processedData)}
                            {renderNode(processedData)}
                        </g>
                    </svg>
                </div>

                <div className="p-4 bg-purple-900/10 border-t border-purple-900/30">
                    <div className="flex gap-4 items-center text-sm text-gray-300">
                        <span className="font-medium text-purple-400">Topic Types:</span>
                        <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-blue-600 mr-2"></div>
                            Main Topic
                        </div>
                        <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-purple-600 mr-2"></div>
                            Subtopic
                        </div>
                        <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-red-600 mr-2"></div>
                            Definition
                        </div>
                        <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-green-600 mr-2"></div>
                            Example
                        </div>
                        <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-gray-600 mr-2"></div>
                            Code
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomMindmapViewer;